import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';
import type { SiteDetailsInput } from './quotes.ts';

// CB-REQ JOB-01 as built at v1.946. A job is created by accepting a quote (api/quotes.ts), so there
// is no create here. A job carries no money: its lines are what goes out, not what it costs.

export type Job = components['schemas']['JobView'];
export type JobSummary = components['schemas']['JobSummaryView'];
export type JobLine = components['schemas']['JobLineView'];
export type Fulfilment = components['schemas']['FulfilmentView'];

/** What a person enters: wall-clock times in the zone of the quote or job. A leg may wait. */
export type FulfilmentInput =
  | { kind: 'collection' }
  | { kind: 'on_site' }
  | {
      kind: 'delivery';
      outAt?: string | null;
      outAddress?: string | null;
      returnAt?: string | null;
      returnAddress?: string | null;
    };

export type JobChanges = {
  name?: string;
  clientPo?: string | null;
  notes?: string | null;
  fulfilment?: FulfilmentInput;
  venueId?: string | null;
  /** CLI-01 at v1.984: null for nobody. */
  accountManagerId?: string | null;
  /** CRW-11 at v1.997: someone on one of its tasks; null for nobody. */
  leadCrewMemberId?: string | null;
  /** JOB-01 at v1.958: a produced event's site contact and load notes. */
  site?: SiteDetailsInput;
  /** CMP-02 at v1.967: whether this job's shifts need SWMS. */
  swmsRequired?: boolean;
  /** VEN-11 at v1.988: how the venue is billed, until the invoice issues. */
  supplyModel?: 'standard' | 'resale' | 'direct_bill';
  /** VEN-11 at v1.989: null clears it. */
  endClientName?: string | null;
};

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const jobsQuery = queryOptions({
  queryKey: ['jobs'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/jobs');
    if (error) throw problem(error, 'Could not load the jobs.');
    return data;
  },
});

export const jobQuery = (id: string) =>
  queryOptions({
    queryKey: ['jobs', id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/jobs/{id}', { params: { path: { id } } });
      if (error) throw problem(error, 'Could not load that job.');
      return data;
    },
  });

/** JOB-10 at v1.951: refused once anything has gone out or the invoice has issued. */
export async function cancelJob(id: string, reason: string) {
  const { data, error } = await api.POST('/v1/jobs/{id}/cancel', {
    params: { path: { id } },
    body: { reason },
  });
  if (error) throw problem(error, 'Could not cancel that job.');
  return data;
}

export async function updateJob(id: string, changes: JobChanges) {
  const { data, error } = await api.PATCH('/v1/jobs/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not change that job.');
  return data;
}

export type ChosenUnit = components['schemas']['ChosenUnitView'];
/** A unit as the warehouse knows it: what a chosen unit and a unit on offer have in common. */
export type Unit = Pick<ChosenUnit, 'assetId' | 'assetTag' | 'serialNumber' | 'status'>;
export type UnitOption = components['schemas']['UnitOptionView'];

/** INV-11 at v1.948: a unit the way the warehouse names it — the tag, else the serial. */
export function unitLabel(u: Unit): string {
  return u.assetTag ?? u.serialNumber ?? 'Removed unit';
}

/** Where a unit is, when that is not simply on the shelf. Null when it may be allocated. */
export function unitAway(u: Unit): string | null {
  const away: Partial<Record<Unit['status'], string>> = {
    in_transit: 'In transit',
    in_repair: 'In repair',
    quarantined: 'Quarantined',
    lost: 'Lost',
    retired: 'Retired',
  };
  return away[u.status] ?? null;
}

export const unitOptionsQuery = (jobId: string, lineId: string) =>
  queryOptions({
    queryKey: ['jobs', jobId, 'lines', lineId, 'units'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/jobs/{id}/lines/{lineId}/units', {
        params: { path: { id: jobId, lineId } },
      });
      if (error) throw problem(error, 'Could not load the units.');
      return data;
    },
  });

/** INV-11 at v1.948: choose this unit for one reserved unit of the line. */
export async function allocateUnit(jobId: string, lineId: string, assetId: string) {
  const { data, error } = await api.POST('/v1/jobs/{id}/lines/{lineId}/allocations', {
    params: { path: { id: jobId, lineId } },
    body: { assetId },
  });
  if (error) throw problem(error, 'Could not allocate that unit.');
  return data;
}

export async function releaseUnit(jobId: string, lineId: string, assetId: string) {
  const { data, error } = await api.POST(
    '/v1/jobs/{id}/lines/{lineId}/allocations/{assetId}/release',
    { params: { path: { id: jobId, lineId, assetId } } },
  );
  if (error) throw problem(error, 'Could not release that unit.');
  return data;
}

/**
 * WHS-16 at v1.949: a bulk line goes whole; a serialised line sends the units named, or every
 * chosen unit not yet out. WHS-09's refusals come back as problems to show as they are.
 */
export async function bookOutLine(jobId: string, lineId: string, assetIds?: string[]) {
  const { data, error } = await api.POST('/v1/jobs/{id}/lines/{lineId}/book-out', {
    params: { path: { id: jobId, lineId } },
    body: assetIds ? { assetIds } : {},
  });
  if (error) throw problem(error, 'Could not book that out.');
  return data;
}

export async function checkInLine(
  jobId: string,
  lineId: string,
  input:
    | { units: Array<{ assetId: string; condition: 'returned' | 'damaged' | 'missing' }> }
    | { returned: number; damaged: number; missing: number },
) {
  const { data, error } = await api.POST('/v1/jobs/{id}/lines/{lineId}/check-in', {
    params: { path: { id: jobId, lineId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not check that in.');
  return data;
}

/** AVL-03 at v1.946: reserve a line accepted short. Refused while the stock is still not there. */
export async function reserveJobLine(jobId: string, lineId: string) {
  const { data, error } = await api.POST('/v1/jobs/{id}/lines/{lineId}/reserve', {
    params: { path: { id: jobId, lineId } },
  });
  if (error) throw problem(error, 'Could not reserve that line.');
  return data;
}

// INV-A13 at v1.957: handing a line's gear straight on to the next job.
export type HandOnTarget = components['schemas']['HandOnTargetView'];

export const handOnTargetsQuery = (jobId: string, lineId: string) =>
  queryOptions({
    queryKey: ['jobs', jobId, 'lines', lineId, 'hand-on-targets'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/jobs/{id}/lines/{lineId}/hand-on-targets', {
        params: { path: { id: jobId, lineId } },
      });
      if (error) throw problem(error, 'Could not load the jobs to hand on to.');
      return data;
    },
  });

export async function handOnLine(
  jobId: string,
  lineId: string,
  input: { toJobId: string; toLineId: string } & ({ quantity: number } | { assetIds: string[] }),
) {
  const { data, error } = await api.POST('/v1/jobs/{id}/lines/{lineId}/hand-on', {
    params: { path: { id: jobId, lineId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not hand that on.');
  return data;
}
